import {createStore, createStoreFromJS, DalJsStore, DalValue, mapToJson, DmStore, ValueTransformer} from './store'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {HistoryItem, Pointer} from '@wix/document-services-types'
import _ from 'lodash'
import {PAGE_FRAGMENT_TYPE} from './fragment/fragmentDalManager'

const jsToFragmentDalStore = (js: FragmentDalJsStore): FragmentStoreMap => {
    const fragmentMapStore = new Map<string, DmStore>()
    Object.entries(js).forEach(([fragmentType, fragmentDmStore]) => {
        fragmentMapStore.set(fragmentType, createStoreFromJS(fragmentDmStore))
    })
    return fragmentMapStore
}

const fragmentMapToJson = (map: Map<string, any>, valueTransformer: ValueTransformer) => {
    const json = {}
    map.forEach((val, key) => {
        if (val._store) val = val._store
        json[key] = val instanceof Map ? mapToJson(val, valueTransformer) : valueTransformer(val)
    })
    return json
}

const cloneFragmentMap = (fragmentMap: FragmentStoreMap): FragmentStoreMap => {
    const clone = new Map<string, DmStore>()
    fragmentMap.forEach((fragmentStore: DmStore, fragmentType: string) => {
        clone.set(fragmentType, fragmentStore.clone())
    })
    return clone
}

type FragmentDalJsStore = Record<string, DalJsStore>
type FragmentStoreMap = Map<string, DmStore>

class FragmentStore {
    public readonly __type = 'FragmentStore'
    readonly _store: FragmentStoreMap

    constructor(initialState?: FragmentStoreMap) {
        this._store = initialState ?? new Map<string, DmStore>()
    }

    static createFromJS(initialState: FragmentDalJsStore): FragmentStore {
        return new FragmentStore(jsToFragmentDalStore(initialState))
    }

    get(pointer: Pointer): DalValue {
        return this._store.get(pointer.fragmentType || PAGE_FRAGMENT_TYPE)?.get(pointer)
    }

    has(pointer: Pointer): boolean {
        return this._store.get(pointer.fragmentType || PAGE_FRAGMENT_TYPE)?.has(pointer) ?? false
    }

    /**
     * @param {Pointer} pointer
     * @param {DalValue} value - value to set
     */
    set(pointer: Pointer, value: DalValue): void {
        const fragmentType = pointer.fragmentType || PAGE_FRAGMENT_TYPE
        if (!this.getFragmentStore(fragmentType)) {
            this.setFragmentStore(fragmentType, createStore())
        }
        this.getFragmentStore(fragmentType)!.set(pointer, value)
    }

    /**
     * Marks the pointer value as deleted by setting the pointer value to undefined
     * @param pointer
     */
    remove(pointer: Pointer): void {
        this.getFragmentStore(pointer.fragmentType || PAGE_FRAGMENT_TYPE)?.set(pointer, undefined)
    }

    /**
     * Deletes the pointer key from the fragmentType store or from pages store if no fragmentType specified in pointer.
     * If there are no longer any ids for the pointer namespace, the namespace is also deleted
     * @param pointer
     */
    deleteKey(pointer: Pointer): void {
        this.getFragmentStore(pointer.fragmentType || PAGE_FRAGMENT_TYPE)?.deleteKey(pointer)
    }

    /**
     * Returns true if the predicate returns true for any pointer-value in the store
     * @param predicate
     */
    some(predicate: (pointer: Pointer, value: any) => boolean) {
        for (const [fragmentType, fragmentDmStore] of this._store.entries()) {
            if (fragmentDmStore.some((pointer, value) => predicate({...pointer, fragmentType}, value))) {
                return true
            }
        }
        return false
    }

    /**
     * an array of history items with where key is the pointer and value is the actual set value, not by fragmentType.
     * the fragmentType will appear in the pointer if exists
     */
    getValues(): HistoryItem[] {
        return [...this._store.values()].flatMap(fragmentStore => fragmentStore.getValues())
    }

    /**
     * returns an array with all the pointers in the store (in all fragment stores, not by fragmentType)
     */
    keys(): Pointer[] {
        return [...this._store.values()].flatMap(fragmentStore => fragmentStore.keys())
    }

    /**
     * filters the fragment stores based on a predicate applied to each DmStore inside the FragmentStore.
     * @param predicate - A function that returns true or false based on the pointer and value.
     */
    filter(predicate: (pointer: Pointer, value: DalValue) => boolean): FragmentStore {
        const newStore = new FragmentStore()

        this._store.forEach((fragmentStore: DmStore, fragmentType: string) => {
            const filteredStore = fragmentStore.filter(predicate)
            if (filteredStore.size() > 0) {
                newStore._store.set(fragmentType, filteredStore)
            }
        })

        return newStore
    }

    /**
     * creates a shallow clone of the fragment store where for each fragmentType,
     * we are omitting the given namespaces from its store
     * this is a more performant way to skip types in iteration
     */
    omit(typesToOmit: string[]): FragmentStore {
        const shallowClonedStore = new Map()
        this._store.forEach((fragmentStore: DmStore, fragmentType: string) => {
            shallowClonedStore.set(fragmentType, fragmentStore.omit(typesToOmit))
        })
        return new FragmentStore(shallowClonedStore)
    }

    intersection(otherStore: FragmentStore): FragmentStore {
        return this.filter(pointer => otherStore.has(pointer))
    }

    /**
     * Returns a deep clone of this store in json format
     */
    cloneAsJson(): FragmentDalJsStore {
        return fragmentMapToJson(this._store, deepClone)
    }

    /**
     * Returns a deep clone of the store
     */
    clone(): FragmentStore {
        return new FragmentStore(cloneFragmentMap(this._store))
    }

    /**
     * Returns the sum of sizes of each DmStore in the fragmentStore
     */
    size(): number {
        let totalSize = 0
        this._store.forEach(dmStore => (totalSize += dmStore.size()))
        return totalSize
    }

    /**
     * get the inner store as Map of fragmentType to fragment store
     */
    getFragmentStoreMap(): FragmentStoreMap {
        return this._store
    }

    hasFragmentStore(fragmentType: string): boolean {
        return !!this.getFragmentStore(fragmentType)
    }

    setFragmentStore(fragmentType: string, fragmentStore: DmStore): void {
        this._store.set(fragmentType, fragmentStore)
    }

    getFragmentStore(fragmentType: string): DmStore | undefined {
        return this._store.get(fragmentType)
    }

    /**
     * Returns a shallow clone of this store in json format
     */
    asJson(): FragmentDalJsStore {
        const jsonStore: FragmentDalJsStore = {}
        this._store.forEach((fragmentStore: DmStore, fragmentType: string) => {
            jsonStore[fragmentType] = fragmentStore.asJson()
        })
        return jsonStore
    }

    /**
     * Copies the entire store to the given object, deleting undefined values and cloning defined values
     * @param {*} js
     */
    applyValuesWithDelete(js: DalJsStore): void {
        this._store.forEach((fragmentStore: DmStore, fragmentType: string) => {
            let fragmentStoreValues = js[fragmentType]
            if (!fragmentStoreValues) {
                fragmentStoreValues = {}
                js[fragmentType] = fragmentStoreValues
            }
            fragmentStore._store.forEach((valuesMap, type) => {
                let typeValues = fragmentStoreValues[type]
                if (!typeValues) {
                    typeValues = {}
                    fragmentStoreValues[type] = typeValues
                }
                fragmentStore._applyValuesMap(valuesMap, typeValues)
            })
        })
    }

    /** Copies the values for the specified type to the given object, deleting undefined values and cloning defined values */
    applyTypeValuesWithDelete(type: string, typeValues: Record<string, any>): void {
        this._store.forEach(fragmentStore => {
            const valuesMap = fragmentStore._store.get(type)
            if (valuesMap) {
                fragmentStore._applyValuesMap(valuesMap, typeValues)
            }
        })
    }

    /**
     * Merge the given store into this store.
     * @param store
     */
    merge(store: FragmentStore): void {
        store._store.forEach((fragmentStore: DmStore, fragmentType: string) => {
            const existingDmStore = this._store.get(fragmentType)
            if (!existingDmStore) {
                this.setFragmentStore(fragmentType, fragmentStore.clone())
            } else {
                existingDmStore.merge(fragmentStore)
            }
        })
    }

    /**
     * true if there are no stored values
     * @return {boolean}
     */
    isEmpty(): boolean {
        return [...this._store.values()].every(fragmentStore => fragmentStore.isEmpty())
    }

    forEach(callback: (fragmentStore: DmStore, fragmentType: string) => void): void {
        this._store.forEach((fragmentStore, fragmentType) => {
            callback(fragmentStore, fragmentType)
        })
    }

    /**
     /** return true if the pointer was removed from the store
     * @param {Pointer} pointer
     * @returns {boolean}
     */
    isRemoved(pointer: Pointer): boolean {
        return this.has(pointer) && _.isUndefined(this.get(pointer))
    }

    /**
     * deltes a fragment type and its store from the fragmentStore
     */
    delete(fragmentType: string): void {
        this._store.delete(fragmentType)
    }

    entries(): IterableIterator<[string, DmStore]> {
        return this._store.entries()
    }
}

function createFragmentStore(initialState?: FragmentStoreMap): FragmentStore {
    return new FragmentStore(initialState)
}

function createFragmentStoreFromJS(initialState: FragmentDalJsStore): FragmentStore {
    return FragmentStore.createFromJS(initialState)
}

export {
    FragmentStore,
    FragmentDalJsStore,
    FragmentStoreMap,
    createFragmentStore,
    createFragmentStoreFromJS,
    jsToFragmentDalStore
}
